import './CfiRoutingControl.scss'
import { useEffect, useState } from "react";
import { Box, Item } from "devextreme-react/box";
import { Field, Panel } from "../../../../layouts";
import SelectBox from "devextreme-react/select-box";
import { useAuth } from "../../../../contexts/auth";
import { useGetCfiCompanies } from "../api/get-cfi-companies";
import DateBox from "devextreme-react/date-box";
import NumberBox from "devextreme-react/number-box";
import { RadioGroup } from "devextreme-react/radio-group";
import CheckBox from "devextreme-react/check-box";
import { useGetCfiRouting } from "../api/get-cfi-routing";
import { Button } from "devextreme-react/button";
import { useUpdateCfiRoutingMutation } from "../api/update-cfi-routing";
import { showErrorMessage, showSuccessMessage } from "../../../../utils/messages";
import { formatDate } from "devextreme/localization";

export default function CfiRoutingPage() {
    const { user } = useAuth();
    const cfiCompanies = useGetCfiCompanies(user?.companyId);
    const [selectedCfiId, setSelectedCfiId] = useState<number | null>(null);
    const cfiRouting = useGetCfiRouting(selectedCfiId, user?.companyId);

    //Routing fields
    const [cutoffTime, setCutoffTime] = useState<Date>(new Date());
    const [cutoffDaysBefore, setCutoffDaysBefore] = useState<number>(1);
    const [selectedRecurrencePattern, setSelectedRecurrencePattern] = useState<number>(1);
    const [recurrenceWeeklyPeriodicity, setRecurrenceWeeklyPeriodicity] = useState<number>(1);
    const [recurrenceWeeklyWeekDays, setRecurrenceWeeklyWeekDays] = useState<number>(31);
    const [recurrenceMonthlyDay, setRecurrenceMonthlyDay] = useState<number>(1);
    const [recurrenceMonthlyDayOfEveryMonth, setRecurrenceMonthlyDayOfEveryMonth] = useState<number>(1);
    const [recurrenceYearlyPeriodicity, setRecurrenceYearlyPeriodicity] = useState<number>(1);
    const [recurrenceYearlyOnMonth, setRecurrenceYearlyOnMonth] = useState<number>(1);
    const [recurrenceYearlyOnMonthDay, setRecurrenceYearlyOnMonthDay] = useState<number>(1);
    const [rangeOfRecurrenceStatus, setRangeOfRecurrenceStatus] = useState<number>(0);
    const [rangeOfRecurrenceStartDate, setRangeOfRecurrenceStartDate] = useState<Date>(new Date());
    const [rangeOfRecurrenceEndDate, setRangeOfRecurrenceEndDate] = useState<Date>(new Date())
    const [occurrence, setOccurrence] = useState<number>(1)

    const updateCfiRoutingMutation = useUpdateCfiRoutingMutation(
        {
            companyID: selectedCfiId ?? 0,
            cutoffTime: formatDate(cutoffTime, 'HH:mm:ss'),
            cutoffDaysBefore: cutoffDaysBefore,
            recurrencePatternType: selectedRecurrencePattern,
            recurrenceWeeklyPeriodicity: selectedRecurrencePattern === 2 ? recurrenceWeeklyPeriodicity : null,
            recurrenceWeeklyWeekDays: selectedRecurrencePattern === 2 ? recurrenceWeeklyWeekDays : null,
            recurrenceMonthlyDay: selectedRecurrencePattern === 3 ? recurrenceMonthlyDay : null,
            recurrenceMonthlyDayOfEveryMonth: selectedRecurrencePattern === 3 ? recurrenceMonthlyDayOfEveryMonth : null,
            recurrenceYearlyPeriodicity: selectedRecurrencePattern === 4 ? recurrenceYearlyPeriodicity : null,
            recurrenceYearlyOnMonth: selectedRecurrencePattern === 4 ? recurrenceYearlyOnMonth : null,
            recurrenceYearlyOnMonthDay: selectedRecurrencePattern === 4 ? recurrenceYearlyOnMonthDay : null,
            rangeOfRecurrenceStartDate: formatDate(rangeOfRecurrenceStartDate, 'yyyy-MM-dd'),
            rangeOfRecurrenceEndDate: rangeOfRecurrenceStatus === 2 ? formatDate(rangeOfRecurrenceEndDate, 'yyyy-MM-dd') : null,
            rangeOfRecurrenceStatus: rangeOfRecurrenceStatus,
            occurrence: rangeOfRecurrenceStatus === 1 ? occurrence : null,
        }, () => {
            showSuccessMessage('CFI Routing has been updated');
            cfiRouting.refetch();
        },
        (error) => showErrorMessage(error))


    useEffect(() => {
        if (cfiRouting.data) {
            setCutoffTime(new Date('2000-01-01 ' + cfiRouting.data.cutoffTime));
            setCutoffDaysBefore(cfiRouting.data.cutoffDaysBefore);
            setSelectedRecurrencePattern(cfiRouting.data.recurrencePatternType);
            setRecurrenceWeeklyPeriodicity(cfiRouting.data.recurrenceWeeklyPeriodicity ?? 1);
            setRecurrenceWeeklyWeekDays(cfiRouting.data.recurrenceWeeklyWeekDays ?? 31);
            setRecurrenceMonthlyDay(cfiRouting.data.recurrenceMonthlyDay ?? 1);
            setRecurrenceMonthlyDayOfEveryMonth(cfiRouting.data.recurrenceMonthlyDayOfEveryMonth ?? 1);
            setRecurrenceYearlyPeriodicity(cfiRouting.data.recurrenceYearlyPeriodicity ?? 1);
            setRecurrenceYearlyOnMonth(cfiRouting.data.recurrenceYearlyOnMonth ?? 1);
            setRecurrenceYearlyOnMonthDay(cfiRouting.data.recurrenceYearlyOnMonthDay ?? 1);
            setRangeOfRecurrenceStatus(cfiRouting.data.rangeOfRecurrenceStatus);
            setRangeOfRecurrenceStartDate(new Date(cfiRouting.data.rangeOfRecurrenceStartDate));
            let date = new Date();
            date.setFullYear(date.getFullYear() + 1);
            setRangeOfRecurrenceEndDate(new Date(cfiRouting.data.rangeOfRecurrenceEndDate ?? date));
            setOccurrence(cfiRouting.data.occurrence ?? 1);
        }
    }, [cfiRouting.data])

    const recurrencePatternData = [
        { id: 1, text: 'Daily' },
        { id: 2, text: 'Weekly' },
        { id: 3, text: 'Monthly' },
        { id: 4, text: 'Yearly' },
    ];

    const weekDays = [
        { id: 1, text: 'Monday' },
        { id: 2, text: 'Tuesday' },
        { id: 4, text: 'Wednesday' },
        { id: 8, text: 'Thursday' },
        { id: 16, text: 'Friday' },
        { id: 32, text: 'Saturday' }
    ]

    const months = [
        { id: 1, text: 'January' },
        { id: 2, text: 'February' },
        { id: 3, text: 'March' },
        { id: 4, text: 'April' },
        { id: 5, text: 'May' },
        { id: 6, text: 'June' },
        { id: 7, text: 'July' },
        { id: 8, text: 'August' },
        { id: 9, text: 'September' },
        { id: 10, text: 'October' },
        { id: 11, text: 'November' },
        { id: 12, text: 'December' }
    ];

    const rangeOfRecurrenceOptions = [
        { id: 0, text: 'No End Date' },
        { id: 1, text: 'Occurrence Qty' },
        { id: 2, text: 'End By' },
    ];

    useEffect(() => {
        if (cfiCompanies.data && cfiCompanies.data.length > 0) {
            setSelectedCfiId(cfiCompanies.data[0].companyID);
        }
    }, [cfiCompanies.isLoading]);

    return (<>
        <div style={{ maxWidth: '700px', minWidth: '700px' }} className="cfi-routing">
            <Panel header="CFI Routing Selection" isLoading={cfiCompanies.isLoading}>
                <Box>
                    <Item ratio={1}>
                        <Field label='CFI Company' className='cfi-routing-field'>
                            <SelectBox
                                value={selectedCfiId}
                                onValueChanged={(e) => setSelectedCfiId(e.value)}
                                dataSource={cfiCompanies?.data}
                                displayExpr="companyName"
                                valueExpr="companyID"
                                searchEnabled={true}
                            >
                            </SelectBox>
                        </Field>
                    </Item>
                </Box>
            </Panel>
            <h3>Valid Delivery Days and Time</h3>
            {
                !cfiRouting.data?.isDefault &&
                <div className='saved-message'>Your selections have been saved. If further changes need to be made, please contact Sage Surfaces.</div>
            }
            <Panel header="Order Cut-off" isLoading={cfiRouting.isLoading}>
                <Box width={400} disabled={selectedCfiId === null || !cfiRouting.data?.isDefault}>
                    <Item ratio={1}>
                        <Field label='Cut-off Time' className='cfi-routing-field'>
                            <DateBox
                                showClearButton={false}
                                type="time"
                                value={cutoffTime}
                                onValueChanged={e => setCutoffTime(e.value)}
                                disabled={selectedCfiId === null}
                            />
                        </Field>
                    </Item>
                    <Item ratio={1}>
                        <div style={{ marginLeft: '10px' }}>
                            <Field label='Day(s) Before' className='cfi-routing-field'>
                                <NumberBox
                                    min={1}
                                    max={31}
                                    showSpinButtons={true}
                                    width={60}
                                    value={cutoffDaysBefore}
                                    onValueChanged={e => setCutoffDaysBefore(e.value)}
                                    disabled={selectedCfiId === null}
                                />
                            </Field>
                        </div>
                    </Item>
                </Box>
            </Panel>
            <Panel header="Recurrence Pattern" isLoading={cfiRouting.isLoading}
                className="cfi-routing-recurrence-pattern">
                <Box disabled={!cfiRouting.data?.isDefault}>
                    <Item baseSize={100}>
                        <RadioGroup items={recurrencePatternData} defaultValue={selectedRecurrencePattern}
                            displayExpr="text" valueExpr="id" value={selectedRecurrencePattern}
                            className="cfi-routing-recurrence-pattern-radios"
                            disabled={selectedCfiId === null}
                            onValueChanged={(e) => setSelectedRecurrencePattern(e.value)} />
                    </Item>
                    <Item ratio={1}>
                        {(selectedRecurrencePattern === 1) && <span>Recurrence occurs every weekday</span>}
                        {(selectedRecurrencePattern === 2) &&
                            <>
                                <div style={{ flexDirection: "row", display: "flex" }}>
                                    <span>Recur every&nbsp;</span>
                                    <NumberBox
                                        min={1}
                                        showSpinButtons={true}
                                        width={60}
                                        value={recurrenceWeeklyPeriodicity}
                                        onValueChanged={e => setRecurrenceWeeklyPeriodicity(e.value)}
                                    />
                                    <span>&nbsp;week(s) on:</span>
                                </div>
                                <div style={{ height: '10px' }}></div>
                                <div style={{ flexDirection: "row", display: "flex" }}>
                                    {weekDays.map(x => <CheckBox key={x.id} text={x.text}
                                        value={(recurrenceWeeklyWeekDays & x.id) > 0}
                                        onValueChanged={e => {
                                            if (e.value) {
                                                setRecurrenceWeeklyWeekDays(recurrenceWeeklyWeekDays + x.id);
                                            } else {
                                                setRecurrenceWeeklyWeekDays(recurrenceWeeklyWeekDays - x.id);
                                            }
                                        }}
                                        style={{ marginRight: '8px' }}></CheckBox>)}
                                </div>
                            </>}
                        {(selectedRecurrencePattern === 3) && <div style={{ flexDirection: "row", display: "flex" }}>
                            <span>Day&nbsp;</span>
                            <NumberBox
                                min={1}
                                width={60}
                                max={31}
                                value={recurrenceMonthlyDay}
                                onValueChanged={e => setRecurrenceMonthlyDay(e.value)}
                                showSpinButtons={true}
                            />
                            <span>&nbsp;of every&nbsp;</span>
                            <NumberBox
                                min={1}
                                width={60}
                                max={12}
                                value={recurrenceMonthlyDayOfEveryMonth}
                                onValueChanged={e => setRecurrenceMonthlyDayOfEveryMonth(e.value)}
                                showSpinButtons={true}
                            />
                            <span>&nbsp;month(s)</span>
                        </div>}
                        {(selectedRecurrencePattern === 4) && <div style={{ flexDirection: "row", display: "flex" }}>
                            <span>Recur every&nbsp;</span>
                            <NumberBox
                                min={1}
                                width={60}
                                max={10}
                                value={recurrenceYearlyPeriodicity}
                                onValueChanged={e => setRecurrenceYearlyPeriodicity(e.value)}
                                showSpinButtons={true}
                            />
                            <span>&nbsp;year(s) on&nbsp;</span>
                            <SelectBox dataSource={months} valueExpr="id" displayExpr="text"
                                disabled={!cfiRouting.data?.isDefault}
                                value={recurrenceYearlyOnMonth}
                                onValueChanged={e => setRecurrenceYearlyOnMonth(e.value)}
                                width={120}></SelectBox>
                            &nbsp;
                            <NumberBox
                                min={1}
                                width={60}
                                max={10}
                                value={recurrenceYearlyOnMonthDay}
                                onValueChanged={e => setRecurrenceYearlyOnMonthDay(e.value)}
                                showSpinButtons={true}
                            />
                        </div>}
                    </Item>
                </Box>
            </Panel>
            <Panel header="Range of Recurrence" isLoading={cfiRouting.isLoading}>
                <Box disabled={selectedCfiId === null || !cfiRouting.data?.isDefault}>
                    <Item ratio={1}>
                        <div style={{ flexDirection: "row", display: "flex" }}>
                            <span>Start:&nbsp;</span>
                            <DateBox
                                width={140}
                                showClearButton={false}
                                value={rangeOfRecurrenceStartDate}
                                onValueChanged={e => setRangeOfRecurrenceStartDate(e.value)}
                                disabled={selectedCfiId === null || !cfiRouting.data?.isDefault}
                            >
                            </DateBox>
                            <span>&nbsp;</span>
                            <SelectBox
                                dataSource={rangeOfRecurrenceOptions}
                                valueExpr="id"
                                displayExpr="text"
                                onValueChanged={(e) => setRangeOfRecurrenceStatus(e.value)}
                                value={rangeOfRecurrenceStatus}
                                disabled={selectedCfiId === null || !cfiRouting.data?.isDefault}
                                width={140}></SelectBox>
                            &nbsp;
                            {(rangeOfRecurrenceStatus === 1) && <NumberBox
                                min={1}
                                width={80}
                                value={occurrence}
                                disabled={!cfiRouting.data?.isDefault}
                                onValueChanged={e => setOccurrence(e.value)}
                                showSpinButtons={true}
                            />}
                            {(rangeOfRecurrenceStatus === 2) && <DateBox
                                showClearButton={false}
                                width={140}
                                disabled={!cfiRouting.data?.isDefault}
                                value={rangeOfRecurrenceEndDate}
                                onValueChanged={e => setRangeOfRecurrenceEndDate(e.value)}
                            />}
                        </div>
                    </Item>
                </Box>
            </Panel>
        </div>
        <Button text="Save"
            disabled={selectedCfiId === null || !cfiRouting.data?.isDefault || updateCfiRoutingMutation.isLoading}
            width={100}
            onClick={() => updateCfiRoutingMutation.mutate()}></Button>
    </>)
}